import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { DataService } from './data';
import { LanguageService } from './language.service';
import {
  formatMissionDecimal,
  formatMissionMoney,
  normalizeMoneda,
  resolveCurrencyLocale,
} from '../utils/currency.util';

@Injectable({ providedIn: 'root' })
export class MissionCurrencyService {
  private readonly data = inject(DataService);
  private readonly language = inject(LanguageService);

  readonly currencyCode$: Observable<string> = this.data.missionMoneda$;

  getCurrencyCode(): string {
    return normalizeMoneda(this.data.getMissionMoneda());
  }

  private currentUiLang(): 'es' | 'it' {
    return this.language.getCurrentLang() === 'it' ? 'it' : 'es';
  }

  resolveLocale(uiLang?: 'es' | 'it'): string {
    return resolveCurrencyLocale(this.getCurrencyCode(), uiLang ?? this.currentUiLang());
  }

  format(
    value: number | null | undefined,
    opts?: { style?: 'currency' | 'decimal'; uiLang?: 'es' | 'it' }
  ): string {
    const style = opts?.style ?? 'currency';
    const uiLang = opts?.uiLang ?? this.currentUiLang();
    const code = this.getCurrencyCode();
    if (style === 'decimal') {
      return formatMissionDecimal(value, code, uiLang);
    }
    return formatMissionMoney(value, code, uiLang, 'currency');
  }

  formatDecimal(value: number | null | undefined, uiLang?: 'es' | 'it'): string {
    return formatMissionDecimal(value, this.getCurrencyCode(), uiLang ?? this.currentUiLang());
  }

  localeForPipe(uiLang?: 'es' | 'it'): Observable<string> {
    return this.currencyCode$.pipe(map(() => this.resolveLocale(uiLang)));
  }
}
